/**
 * Blog post loader + draft parser — #107 / punch-list B8.
 *
 * The 12 launch posts are authored as markdown drafts in src/content/blog/
 * (vendored from /shared/blog/ — that folder is not in this repo). Each draft
 * carries:
 *   - an HTML-comment SEO/AEO metadata header (Title tag, Meta description,
 *     URL slug, Primary keyword, Pillar);
 *   - the article body — an H1, a "**Short answer:**" lede, then ## sections;
 *   - a "## Frequently asked questions" section;
 *   - a "## Structured data" section with two ```json blocks: BlogPosting
 *     (datePublished / dateModified ship as a `{{PUBLISH_DATE}}` token the
 *     build must stamp) + FAQPage (final — used verbatim).
 *
 * DAILY DRIP (B8, 2026-05-22). Each post is stamped with a distinct date —
 * its drip slot from blog-routes.js (LAUNCH_DATE + order). A post is "live"
 * only once that date has arrived; BLOG_POSTS is the live subset, so the
 * index + routes reveal one post per day with no redeploy. The BlogPosting
 * JSON-LD's datePublished + dateModified are both that distinct date
 * (dateModified = datePublished at launch — bump only on a real revision).
 *
 * The on-page FAQ accordions render from the FAQPage JSON-LD's mainEntity,
 * keeping the visible answers verbatim-matched to the schema (a Google
 * requirement for FAQ rich results).
 */

import { BLOG_ROUTES, isPostLive } from "./blog-routes.js";

function safeParseJson(text) {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/**
 * Parse one raw markdown draft into a structured blog post. The BlogPosting
 * JSON-LD keeps its `{{PUBLISH_DATE}}` token here — the date is stamped in
 * the manifest join below, where each post's distinct drip date is known.
 * Exported for unit testing.
 * @param {string} raw
 * @returns {object}
 */
export function parseBlogDraft(raw) {
  const post = {
    title: "",
    description: "",
    slug: "",
    keyword: "",
    shortAnswer: "",
    body: "",
    faqs: [],
    blogPostingLD: null,
    faqPageLD: null,
  };

  // ── 1. Metadata header (the leading HTML comment) ──
  const metaMatch = raw.match(/<!--([\s\S]*?)-->/);
  const meta = metaMatch ? metaMatch[1] : "";
  const metaField = (label) => {
    const m = meta.match(new RegExp("^\\s*" + label + ":\\s*(.+)$", "m"));
    return m ? m[1].trim() : "";
  };
  post.title = metaField("Title tag");
  post.description = metaField("Meta description");
  post.keyword = metaField("Primary keyword");
  post.slug = metaField("URL slug")
    .replace(/^\/?blog\//, "")
    .replace(/^\/+|\/+$/g, "")
    .trim();

  const content = metaMatch
    ? raw.slice(metaMatch.index + metaMatch[0].length)
    : raw;

  // ── 2. Structured data — the two ```json blocks ──
  const jsonBlocks = [...content.matchAll(/```json\s*([\s\S]*?)```/g)].map((m) =>
    m[1].trim(),
  );
  post.blogPostingLD = safeParseJson(jsonBlocks[0]);
  post.faqPageLD = safeParseJson(jsonBlocks[1]);

  // ── 3. Article part — everything before "## Structured data" ──
  const sdIdx = content.search(/\n#+\s+Structured data/i);
  const articlePart = sdIdx >= 0 ? content.slice(0, sdIdx) : content;

  // ── 4. Body — before "## Frequently asked questions" ──
  const faqIdx = articlePart.search(/\n#+\s+Frequently asked questions/i);
  let body = (faqIdx >= 0 ? articlePart.slice(0, faqIdx) : articlePart).trim();

  // Drop the leading H1 — the page renders the title from metadata.
  body = body.replace(/^#\s+.+$/m, "").trim();

  // Pull the "**Short answer:**" lede into its own field (the template
  // renders it in the AEO answer box, not inline).
  const saMatch = body.match(/^\*\*Short answer:\*\*\s*(.+)$/m);
  if (saMatch) {
    post.shortAnswer = saMatch[1].trim();
    body = (body.slice(0, saMatch.index) + body.slice(saMatch.index + saMatch[0].length)).trim();
  }
  post.body = body;

  // ── 5. FAQ — from the FAQPage JSON-LD (verbatim-matched to the schema) ──
  post.faqs = (post.faqPageLD?.mainEntity || []).map((q) => ({
    q: q.name || "",
    a: q.acceptedAnswer?.text || "",
  }));

  return post;
}

/**
 * Stamp the BlogPosting JSON-LD's date tokens with the post's distinct drip
 * date. dateModified = datePublished at launch (B8). Returns a fresh object.
 */
function stampDates(blogPostingLD, publishDate) {
  if (!blogPostingLD) return null;
  return { ...blogPostingLD, datePublished: publishDate, dateModified: publishDate };
}

// Vendored draft content — Vite inlines every src/content/blog/*.md as a raw
// string at build time. Plain-Node tooling (prerender.js, gen-sitemap.js)
// must NOT import this module; it reads the route list from blog-routes.js.
const RAW_DRAFTS = import.meta.glob("../content/blog/*.md", {
  query: "?raw",
  import: "default",
  eager: true,
});

const parsedBySlug = new Map();
for (const raw of Object.values(RAW_DRAFTS)) {
  const post = parseBlogDraft(raw);
  if (post.slug) parsedBySlug.set(post.slug, post);
}

/**
 * All 12 posts, ordered by pillar priority, each merged with its manifest
 * entry (topic / readMin / featured / order / publishDate) and its
 * BlogPosting dates stamped. NOT date-gated — exported for build tooling +
 * tests. App surfaces should use BLOG_POSTS (the live subset).
 */
export const ALL_BLOG_POSTS = BLOG_ROUTES.map((route) => {
  const parsed = parsedBySlug.get(route.slug);
  if (!parsed) return null;
  return {
    ...parsed,
    ...route,
    blogPostingLD: stampDates(parsed.blogPostingLD, route.publishDate),
  };
}).filter(Boolean);

/**
 * The posts whose drip date has arrived — the set the index + routes show.
 * Evaluated at module load, so the drip advances on any fresh page load.
 */
export const BLOG_POSTS = ALL_BLOG_POSTS.filter((p) => isPostLive(p.publishDate));

/** The single featured post (pillar #1) among the live set. */
export const FEATURED_POST =
  BLOG_POSTS.find((p) => p.featured) || BLOG_POSTS[0] || null;

/** Look up one live post by slug. A not-yet-dripped slug returns null. */
export function getBlogPost(slug) {
  return BLOG_POSTS.find((p) => p.slug === slug) || null;
}

/**
 * Up to three "keep reading" posts — the next live posts in pillar order,
 * wrapping around. Deterministic; every post links onward where it can.
 */
export function relatedPosts(slug, count = 3) {
  if (BLOG_POSTS.length <= 1) return [];
  const idx = BLOG_POSTS.findIndex((p) => p.slug === slug);
  const start = idx < 0 ? 0 : idx;
  const out = [];
  for (let i = 1; i <= count && i < BLOG_POSTS.length; i += 1) {
    out.push(BLOG_POSTS[(start + i) % BLOG_POSTS.length]);
  }
  return out;
}
